function normalizeIdentityPart(value) {
  return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : null;
}

/**
 * Return the stable identity fields exposed by either CurrentAccount or /me.
 * Wallet address is authoritative; username is only a compatibility fallback.
 */
export function getSessionIdentity(account) {
  return {
    walletAddress: normalizeIdentityPart(account?.walletAddress),
    username: normalizeIdentityPart(account?.username)
  };
}

export function isSameSessionIdentity(currentAccount, nextAccount) {
  const current = getSessionIdentity(currentAccount);
  const next = getSessionIdentity(nextAccount);

  if (current.walletAddress || next.walletAddress) {
    return (
      !!current.walletAddress &&
      !!next.walletAddress &&
      current.walletAddress === next.walletAddress
    );
  }

  return !!current.username && !!next.username && current.username === next.username;
}

function hasSessionIdentity(account) {
  const identity = getSessionIdentity(account);
  return !!identity.walletAddress;
}

export function isSessionTransportReady(currentAccount, transportAccount, transitioning = false) {
  return (
    !transitioning &&
    hasSessionIdentity(currentAccount) &&
    isSameSessionIdentity(currentAccount, transportAccount)
  );
}

const CLEAR_ACCOUNT_CACHE_MESSAGE = 'sogni-vibe:clear-account-replay-cache';
const cacheResetCoordinators = new WeakSet();

function clearLocalSessionSocketAccountCaches(socket) {
  if (!socket) return;
  if ('_lastBalanceUpdate' in socket) socket._lastBalanceUpdate = null;
  if ('_lastSubscriptionEntitlement' in socket) socket._lastSubscriptionEntitlement = null;
}

/**
 * CurrentAccount._clear() does not reset AccountApi's subscription recency
 * guards in SDK 5.1. Reset the detected fields so user B's lower entitlement
 * version cannot be discarded as stale relative to user A.
 */
export function clearSessionAccountApiCaches(accountApi) {
  if (!accountApi || typeof accountApi !== 'object') return false;

  let cleared = false;
  if ('lastAppliedSubscriptionVersion' in accountApi) {
    accountApi.lastAppliedSubscriptionVersion = null;
    cleared = true;
  }
  if ('appliedSubscriptionSocketWrites' in accountApi) {
    accountApi.appliedSubscriptionSocketWrites = 0;
    cleared = true;
  }
  return cleared;
}

/**
 * Extend SDK 5.1's coordinator callback with one Vibe-only control message so
 * a secondary tab can clear the primary tab's account replay caches too.
 */
export function installSessionSocketCacheResetHandler(socket) {
  const coordinator = socket?.coordinator;
  const callbacks = coordinator?.callbacks;
  if (
    !callbacks ||
    typeof callbacks.onMessage !== 'function' ||
    cacheResetCoordinators.has(coordinator)
  ) {
    return false;
  }

  const originalOnMessage = callbacks.onMessage;
  callbacks.onMessage = async (message, deadline) => {
    if (message?.type === CLEAR_ACCOUNT_CACHE_MESSAGE) {
      clearLocalSessionSocketAccountCaches(socket);
      return;
    }
    return originalOnMessage(message, deadline);
  };
  cacheResetCoordinators.add(coordinator);
  return true;
}

export async function clearSessionSocketAccountCaches(socket) {
  clearLocalSessionSocketAccountCaches(socket);

  const coordinator = socket?.coordinator;
  if (
    !coordinator ||
    typeof coordinator.isReady !== 'function' ||
    typeof coordinator.sendMessage !== 'function'
  ) {
    return;
  }

  await coordinator.isReady();
  if (!coordinator.isPrimary) {
    await coordinator.sendMessage({ type: CLEAR_ACCOUNT_CACHE_MESSAGE });
  }
}

/**
 * Probe cookie identity without mutating the live SDK account, then sequence an
 * account switch so the old transport is gone before the new identity is
 * published. All side effects are injected to keep the ordering testable.
 */
export async function revalidateSessionIdentity({
  currentAccount,
  transportAccount,
  probeProfile,
  resetSession,
  reconnectSession,
  publishProfile
}) {
  const profile = await probeProfile();
  if (!hasSessionIdentity(profile)) {
    throw new Error('Sogni session profile did not include an account identity');
  }

  const identityChanged =
    !isSameSessionIdentity(currentAccount, profile) ||
    (transportAccount !== undefined && !isSameSessionIdentity(transportAccount, profile));
  if (identityChanged) {
    await resetSession(profile);
    await reconnectSession(profile);
  }

  await publishProfile(profile, { identityChanged });
  return { authenticated: true, identityChanged, profile };
}

/**
 * SDK 5.1's multi-instance secondary disconnect starts a coordinator request
 * but does not await its acknowledgement. Use the coordinator directly when
 * its current implementation is detectable, and fall back to the public API.
 */
export async function disconnectSessionSocket(socket) {
  if (!socket || typeof socket.disconnect !== 'function') {
    throw new Error('Sogni session socket is unavailable');
  }

  const coordinator = socket.coordinator;
  const canAwaitSecondaryDisconnect =
    coordinator &&
    typeof coordinator.isReady === 'function' &&
    typeof coordinator.sendMessage === 'function' &&
    '_wantConnected' in socket;

  if (canAwaitSecondaryDisconnect) {
    // This mirrors BrowserWebSocketClient.disconnect()'s first synchronous
    // assignment, then fixes its missing `await coordinator.sendMessage(...)`.
    socket._wantConnected = false;
    await coordinator.isReady();
    if (!coordinator.isPrimary) {
      await coordinator.sendMessage({ type: 'disconnect' });
      return;
    }
  }

  await socket.disconnect();
}

function waitForSocketEvent(socket, eventName, timeoutMs) {
  let unsubscribe = null;
  let timeoutId = null;

  const promise = new Promise((resolve, reject) => {
    if (typeof socket?.on !== 'function') {
      reject(new Error('Sogni session socket cannot report authentication state'));
      return;
    }

    unsubscribe = socket.on(eventName, resolve);
    timeoutId = setTimeout(() => {
      reject(new Error(`Timed out waiting for Sogni socket ${eventName}`));
    }, timeoutMs);
  });

  return {
    promise,
    cancel() {
      if (timeoutId) clearTimeout(timeoutId);
      if (typeof unsubscribe === 'function') unsubscribe();
    }
  };
}

/**
 * Rebind a cookie-authenticated socket and wait for the server's authenticated
 * event, not merely WebSocket open, before callers expose the new account.
 */
export async function reconnectSessionSocket(
  socket,
  { timeoutMs = 15000, beforeConnect } = {}
) {
  await disconnectSessionSocket(socket);
  if (beforeConnect) await beforeConnect();

  const authenticated = waitForSocketEvent(socket, 'authenticated', timeoutMs);
  try {
    const [, authenticatedSession] = await Promise.all([
      socket.connect(),
      authenticated.promise
    ]);
    return authenticatedSession;
  } finally {
    authenticated.cancel();
  }
}
